/**
 * Tijdlijn en pakketregel van de tabs Bestelling en Retour (keuze 15 en 16).
 * Een vinkje alleen op een stap die de backend gezet noemt; een gezette
 * stap zonder bewaard moment krijgt geen datum (nooit een verzonnen dag).
 * Sessie 163 (herstel): geannuleerd en afgewezen krijgen een kruis in een
 * eigen kleur, open stappen daarvoor heten "vervallen"; bij zeven of meer
 * stappen korte labels zodat de smalle kolom niet overloopt.
 */
import { veiligeLink } from "../opmaak";
import type { KaartPakket, KaartStap } from "./api";
import { dagDatum, dagDatumTijd, dagKort, dagTijdKort, tijdOfDag } from "./hulp";

export function Vink() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** Afsluitende retourstappen (sessie 163): geen vink, eigen stijl. */
const AFSLUITEND = new Set(["geannuleerd", "afgewezen"]);

/**
 * Korte weergave bij zeven of meer stappen (retour): de smalle kaartkolom
 * (360-410 px) en de iPhone hebben geen plek voor "Binnen in het magazijn".
 * De volle tekst blijft in de title en voor de schermlezer. Het zachte
 * afbreekstreepje (\u00AD) breekt een lang woord alleen als het moet.
 */
export const KORT_LABEL: Record<string, string> = {
  aangevraagd: "Aange\u00ADvraagd",
  label_gemaakt: "Label",
  bij_pakketpunt: "Pakket\u00ADpunt",
  onderweg: "Onder\u00ADweg",
  binnen: "Binnen",
  verwerkt: "Verwerkt",
  gecrediteerd: "Gecredi\u00ADteerd",
  geannuleerd: "Geannu\u00ADleerd",
  afgewezen: "Afge\u00ADwezen",
};

const TZ = "Europe/Amsterdam";

/**
 * "6 okt 11:02": dag, maand en tijd in Amsterdamse tijd (werkplek-retour,
 * het ontvangstuur). Een kale datum ("2026-10-06") heeft geen tijd en
 * krijgt alleen de dag; een ander jaar komt erbij.
 */
export function momentMetTijd(iso: string | null | undefined, nu: Date = new Date()): string {
  if (!iso) return "";
  const kaal = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = new Date(kaal ? `${iso}T12:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return "";
  const jaar = (x: Date) => x.toLocaleDateString("nl-NL", { timeZone: TZ, year: "numeric" });
  const dag = d
    .toLocaleDateString("nl-NL", {
      timeZone: TZ,
      day: "numeric",
      month: "short",
      ...(jaar(d) === jaar(nu) ? {} : { year: "numeric" }),
    })
    .replace(/\./g, "");
  if (kaal) return dag;
  const tijd = d.toLocaleTimeString("nl-NL", {
    timeZone: TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${dag} ${tijd}`;
}

function Kruis() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

type StapStand = "ok" | "eind" | "vervallen" | "open";

function standVan(s: KaartStap, afgesloten: boolean): StapStand {
  if (s.gedaan) return AFSLUITEND.has(s.sleutel) ? "eind" : "ok";
  return afgesloten ? "vervallen" : "open";
}

export function Stappen({
  stappen,
  label,
  metTijd = false,
}: {
  stappen: KaartStap[];
  label: string;
  /** Werkplek-retour: moment met tijd ("6 okt 11:02") in plaats van de dag. */
  metTijd?: boolean;
}) {
  // Geannuleerd of afgewezen: wat daarna nog open stond gebeurt niet meer.
  const afgesloten = stappen.some((s) => s.gedaan && AFSLUITEND.has(s.sleutel));
  const lang = stappen.length > 6;
  const klasse = `kk10-tijdlijn${stappen.length > 5 ? " zes" : ""}${lang ? " lang" : ""}`;
  return (
    <ol className={klasse} aria-label={label} lang="nl">
      {stappen.map((s) => {
        const stand = standVan(s, afgesloten);
        const kort = lang ? KORT_LABEL[s.sleutel] : undefined;
        const moment = s.op ? (metTijd ? momentMetTijd(s.op) : dagDatum(s.op)) : "";
        const leeg = stand === "vervallen" ? "vervallen" : stand === "open" ? "nog niet" : "";
        return (
          <li
            key={s.sleutel}
            className={`kk10-stap${stand === "open" ? "" : ` ${stand}`}`}
            data-stap={s.sleutel}
            data-gedaan={s.gedaan ? "ja" : "nee"}
            data-stand={stand}
            title={kort ? s.label : undefined}
          >
            <span className="bol">
              {stand === "ok" ? <Vink /> : stand === "eind" ? <Kruis /> : null}
            </span>
            {kort ? (
              <>
                <b aria-hidden="true">{kort}</b>
                <span className="sr-only">{s.label}</span>
              </>
            ) : (
              <b>{s.label}</b>
            )}
            <span className="num">{moment || leeg}</span>
            {stand === "ok" ? <span className="sr-only">gedaan</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

/** "Onderweg" wordt "onderweg"; een afkorting ("DHL ...") blijft staan. */
function gewoon(woord: string): string {
  if (woord.length > 1 && woord[1] === woord[1].toLowerCase()) {
    return woord[0].toLowerCase() + woord.slice(1);
  }
  return woord;
}

/**
 * De stand in gewone woorden (sessie 177). Bezorgd met dag en tijd, en de
 * afleverplek en handtekening als de vervoerder die meldt. "status onbekend"
 * alleen als er echt niets bekend is.
 */
function pakketStand(pakket: KaartPakket, nu: Date = new Date()): string {
  if (pakket.bezorgd_op) {
    return [
      `bezorgd ${dagDatumTijd(pakket.bezorgd_op, nu)}`,
      pakket.plek ? pakket.plek : null,
      pakket.handtekening === true ? "met handtekening" : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  if (pakket.status) return gewoon(pakket.status);
  if (pakket.verzonden_op) return `verzonden ${dagDatum(pakket.verzonden_op, nu)}`;
  return "status onbekend";
}

/** Waar de stand vandaan komt; leeg als het antwoord het niet zegt (oud of retour). */
function pakketBron(pakket: KaartPakket, nu: Date = new Date()): string {
  if (pakket.live === "bevestigd") {
    const op = tijdOfDag(pakket.live_op, nu);
    return op ? `live bevestigd ${op}` : "live bevestigd";
  }
  if (pakket.live === "niet_bevestigd") {
    const van = dagTijdKort(pakket.stand_van, nu);
    return van ? `niet live bevestigd, stand van ${van}` : "niet live bevestigd";
  }
  if (pakket.live === "ophalen") return "wordt opgehaald";
  return "";
}

/** Tabel en vervoerder zeggen iets anders: kort "onzeker: ...". Anders leeg. */
function pakketVerschil(pakket: KaartPakket, nu: Date = new Date()): string {
  const vervoerder = pakket.bezorgd_op
    ? dagKort(pakket.bezorgd_op, nu)
    : pakket.status
      ? gewoon(pakket.status)
      : "onbekend";
  if (pakket.vorige_bezorgd_op) {
    const tabel = dagKort(pakket.vorige_bezorgd_op, nu);
    const tabelTekst = pakket.bezorgd_op ? tabel : `bezorgd ${tabel}`;
    return `onzeker: tabel ${tabelTekst}, vervoerder ${vervoerder}`;
  }
  if (pakket.vorige_status) {
    return `onzeker: tabel ${gewoon(pakket.vorige_status)}, vervoerder ${vervoerder}`;
  }
  return "";
}

export function PakketRegel({
  pakket,
  titel,
  metBron = false,
}: {
  pakket: KaartPakket;
  titel?: string;
  /**
   * Sessie 177: bron, tijd en verschil tonen (Orderblok). Het retourpakket
   * heeft geen eigen live vraag en laat dit weg.
   */
  metBron?: boolean;
}) {
  const link = veiligeLink(pakket.track_trace_url);
  const naam = titel ?? `Pakket ${pakket.volgnummer} van ${pakket.van}`;
  const bron = metBron ? pakketBron(pakket) : "";
  const verschil = metBron ? pakketVerschil(pakket) : "";
  return (
    <div
      className={`kk10-pakket${bron || verschil ? " met-bron" : ""}`}
      data-testid="pakket"
      data-live={metBron && pakket.live ? pakket.live : undefined}
    >
      <span className="n">{naam}</span>
      <span className="w">
        {pakket.vervoerder ?? "vervoerder onbekend"}{" "}
        {pakket.track_trace_code ? (
          link ? (
            <a href={link} target="_blank" rel="noopener noreferrer">
              {pakket.track_trace_code}
            </a>
          ) : (
            <span className="num">{pakket.track_trace_code}</span>
          )
        ) : (
          <span className="stil">geen track en trace</span>
        )}
      </span>
      <span className="n r" data-testid="pakket-stand">
        {pakketStand(pakket)}
      </span>
      {bron || verschil ? (
        <span className="kk10-bron" data-testid="pakket-bron">
          {bron ? <span className="kk10-bron-tekst">{bron}</span> : null}
          {verschil ? <span className="kk10-onzeker">{verschil}</span> : null}
        </span>
      ) : null}
    </div>
  );
}
